import * as React from 'react';
import parseISO from 'date-fns/parseISO';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { pipe, constNull } from 'fp-ts/lib/function';

import { formatInTimeZone } from 'date-fns-tz';

import { dateFormats } from '../../../utils/dateFormats';
import { FullMatchFragment } from '../../../generated/graphql';
import { KitList } from '../../../components/KitList';
import { makeAvailabilityMap } from '../../../utils/makeAvailabilityMap';
import { ExternalTextLink, TextLink } from '../../../components/ClickableText';
import { SquadList } from '../SquadList';
import { paths } from '../../../paths';
import { sequenceTOption } from '../../../function';
import { EdgeContainer } from '../../../components/Container';
import { Card } from '../../../components/Card';
import { OverlayCardContainer } from '../components/OverlayCardContainer';
import { CardHeading } from '../../../components/CardHeading';
import { JoinTeamPrompt } from '../components/JoinTeamPrompt';
import { useAuth } from '../../../contexts/AuthContext';
import { PayCaptainLink } from '../components/PayCaptainLink';
import { MiniAvailabilityChooser } from '../../../components/MiniAvailabilityChooser';
import { isNil } from '../../../utils/isNil';

import { getMapsLink } from '../../../utils/getMapsLink';
import { mapImageUrl } from '../../../utils/mapImageUrl';
import { IconHandshake } from '../../../icons/Handshake';
import { PreMatchPrompts } from './PreMatchPrompts';

interface Props {
  match: FullMatchFragment;
}

export const PreMatchDetails: React.FC<Props> = ({ match }) => {
  const { user } = useAuth();
  const playedAt = parseISO(match.playedAt);
  const meetAt = pipe(
    O.fromNullable(match.meetAt),
    O.map(parseISO),
    O.map((timestamp) =>
      formatInTimeZone(timestamp, match.timeZone, dateFormats.time),
    ),
  );

  const location = O.fromNullable(match.location);

  const userAvailability = O.fromNullable(match.availability);

  return (
    <main>
      <EdgeContainer className="space-y-6 bg-grey-50 px-6 -mb-6 pb-6">
        <OverlayCardContainer>
          <Card className="bg-white change-transform overflow-hidden">
            {pipe(
              location,
              O.fold(
                () => null,
                ({ lat, lng, address }) => (
                  <a
                    href={getMapsLink({ address, lat, lng })}
                    target="_blank"
                    className="block -mx-4 -mt-4 mb-6 h-56"
                    title="Open in maps"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={mapImageUrl([lat, lng], { width: 750, height: 224 })}
                      className="block h-full object-cover object-center"
                    />
                  </a>
                ),
              ),
            )}
            <dl className="grid grid-cols-4 gap-4 mb-4">
              <div className="space-y-4 col-span-3">
                <div>
                  <dt className="text-xs uppercase font-semibold">Date</dt>
                  <dd>
                    <time dateTime={match.playedAt}>
                      {formatInTimeZone(
                        playedAt,
                        match.timeZone,
                        dateFormats.longDate,
                      )}
                    </time>
                  </dd>
                </div>
                {pipe(
                  location,
                  O.bind('firstLine', ({ address }) =>
                    A.head(address.split(',')),
                  ),
                  O.map(({ address, firstLine, lat, lng }) => (
                    <div key="1">
                      <dt className="text-xs uppercase font-semibold">
                        Location
                      </dt>

                      <dd>
                        <ExternalTextLink
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in maps"
                          href={getMapsLink({ address, lat, lng })}
                        >
                          {firstLine}
                        </ExternalTextLink>
                      </dd>
                    </div>
                  )),
                  O.toNullable,
                )}
              </div>
              <div className="text-right space-y-4 col-span-1">
                {pipe(
                  meetAt,
                  O.fold(constNull, (meet) => (
                    <div>
                      <dt className="text-xs uppercase font-semibold">Meet</dt>
                      <dd>{meet}</dd>
                    </div>
                  )),
                )}
                <div>
                  <dt className="text-xs uppercase  font-semibold">
                    Match Time
                  </dt>
                  <dd>
                    {formatInTimeZone(
                      playedAt,
                      match.timeZone,
                      dateFormats.time,
                    )}
                  </dd>
                </div>
              </div>
            </dl>

            {match.team.isCaptain ? (
              <p className="text-sm text-right">
                <TextLink to={paths.match.settings.details(match.id)}>
                  Edit
                </TextLink>
              </p>
            ) : null}

            <div className="space-y-2 mt-4 flex justify-between items-end">
              {pipe(
                sequenceTOption(
                  userAvailability,
                  O.fromNullable(match.team.player?.id),
                ),
                O.fold(
                  () => (
                    <TextLink
                      to={paths.match.availability(match.id)}
                      className="text-sm"
                    >
                      Set your availability
                    </TextLink>
                  ),
                  ([availability, playerId]) => (
                    <div key={availability}>
                      <CardHeading className="mb-2">Availability</CardHeading>
                      <MiniAvailabilityChooser
                        currentAvailabililty={availability}
                        matchId={match.id}
                        playerId={playerId}
                      />
                    </div>
                  ),
                ),
              )}

              {match.excludedFromStats ? (
                <div title="Friendly. This match does not count towards stats and leaderboards.">
                  <IconHandshake className="text-grey-600 w-7 h-auto" />
                </div>
              ) : null}
            </div>
          </Card>
        </OverlayCardContainer>

        {isNil(match.team.player) && !match.team.isCaptain ? (
          <JoinTeamPrompt team={match.team} />
        ) : null}

        {match.team.isCaptain ? <PreMatchPrompts match={match} /> : null}

        {match.players.length > 0 ? (
          <Card className="space-y-4 bg-white">
            <CardHeading>Team</CardHeading>
            <SquadList
              isSignedIn={!isNil(user)}
              players={match.players}
              availabilities={makeAvailabilityMap(match.availabilities)}
            />
            {match.team.isCaptain ? (
              <p className="text-sm text-right">
                <TextLink to={paths.match.settings.squad(match.id)}>
                  Edit
                </TextLink>
              </p>
            ) : null}
          </Card>
        ) : null}

        {pipe(
          match.kitItems,
          O.fromPredicate(A.isNonEmpty),
          O.fold(
            () => null,
            (kitItems) => (
              <Card className="space-y-4 bg-white">
                <CardHeading>Kit</CardHeading>
                <KitList kitItems={kitItems} size="sm" />
                {match.team.isCaptain ? (
                  <p className="text-sm text-right">
                    <TextLink to={paths.match.settings.kit(match.id)}>
                      Edit
                    </TextLink>{' '}
                  </p>
                ) : null}
              </Card>
            ),
          ),
        )}

        {match.team.paymentLink && !match.team.isCaptain ? (
          <PayCaptainLink paymentLink={match.team.paymentLink} />
        ) : null}
      </EdgeContainer>
    </main>
  );
};
